import { useState } from "react";
import CarBox from "./CarBox";
import { useTranslation } from "react-i18next";
import useFetch from "../../hooks/useFetch";
import { useEffect } from "react";
import { capitalFirstLetter } from "../../utils";
import { Pagination } from "@mui/material";

const rowPerPage = 5;

function CarSlide() {
  const { items, isLoading } = useFetch({
    module: "cars",
    enabled: true,
  });

  const datas = (items && items?.data) || [];
  const cars = datas.sort((a, b) => {
    const priceA =
      a.prices.find((price) => price.type === "self_drive" && price.condition_type === "default")?.price || 0;
    const priceB =
      b.prices.find((price) => price.type === "self_drive" && price.condition_type === "default")?.price || 0;
    return priceA - priceB;
  });

  const [selectedCar, setSelectedCar] = useState(null);
  const [colorBtn, setColorBtn] = useState("btn0");
  const { t } = useTranslation();

  const btnID = (id) => {
    setColorBtn(colorBtn === id ? "" : id);
  };

  const count = Math.ceil(cars?.length / rowPerPage);
  const [page, setPage] = useState(1);
  const handleChange = (event, value) => {
    const paginate = paginator(cars, value, 5);
    setPage(paginate.page);
    setColorBtn("btn0");
    const selected = cars.find((c) => c.id === paginate.data[0].id);
    setSelectedCar(selected);
  };

  useEffect(() => {
    if (!isLoading && cars) {
      setSelectedCar(cars[0]);
    }
  }, [cars]);

  return (
    <section className="relative py-20 overflow-hidden md:py-28 bg-gradient-to-b from-white via-slate-50 to-white">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#f5272b] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 rounded-full right-1/4 w-96 h-96 bg-slate-800 blur-3xl"></div>
      </div>

      <div className="container relative z-10 px-4 mx-auto md:px-8">
        <div className="mx-auto max-w-[92rem]">
          {/* Header Section */}
          <div className="mb-16 space-y-4 text-center">
            <h3 className="text-[#f5272b] font-semibold text-lg md:text-xl tracking-wide uppercase">
              {t("vehicle_models")}
            </h3>
            <h2 className="text-4xl font-bold leading-tight md:text-5xl lg:text-6xl text-slate-900">
              {t("our_rental_fleet")}
            </h2>
            <p className="max-w-3xl mx-auto text-lg leading-relaxed md:text-xl text-slate-600">{t("choose_from")}</p>
            <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-[#f5272b] to-transparent mx-auto rounded-full"></div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            {/* Car Selection Sidebar */}
            <div className="lg:col-span-4 xl:col-span-3">
              <div className="p-6 bg-white border shadow-xl rounded-3xl md:p-8 border-slate-100 lg:sticky lg:top-8">
                <h3 className="flex items-center gap-3 mb-6 text-xl font-bold md:text-2xl text-slate-900">
                  <div className="w-10 h-10 bg-[#f5272b]/10 rounded-xl flex items-center justify-center">
                    <i className="fa-solid fa-cars text-[#f5272b] text-lg"></i>
                  </div>
                  Select Vehicle
                </h3>

                {/* Car Buttons List */}
                <div className="mb-6 space-y-3">
                  {cars &&
                    paginator(cars, page, rowPerPage).data.map((item, idx) => {
                      const isSelected = colorBtn === `btn${idx}`;
                      return (
                        <button
                          key={idx}
                          className={`w-full text-left px-5 py-4 rounded-xl font-semibold transition-all duration-300 border-2 ${
                            isSelected
                              ? "bg-[#f5272b] text-white border-[#f5272b] shadow-lg shadow-[#f5272b]/30"
                              : "bg-slate-50 text-slate-700 border-slate-200 hover:border-[#f5272b]/30 hover:bg-slate-100"
                          }`}
                          onClick={() => {
                            setSelectedCar(item);
                            btnID(`btn${idx}`);
                          }}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="flex-1 truncate">{capitalFirstLetter(item.name)}</span>
                            {isSelected && <i className="flex-shrink-0 text-xl fa-solid fa-circle-check"></i>}
                          </div>
                        </button>
                      );
                    })}
                </div>

                {/* Pagination */}
                <div className="pt-4 border-t border-slate-200">
                  <Pagination
                    count={count}
                    page={page}
                    onChange={handleChange}
                    variant="outlined"
                    shape="rounded"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                        fontWeight: 600,
                        color: "#475569",
                        "&:hover": {
                          borderColor: "#f5272b",
                          backgroundColor: "#fef2f2",
                        },
                      },
                      "& .Mui-selected": {
                        backgroundColor: "#f5272b !important",
                        color: "white !important",
                        borderColor: "#f5272b !important",
                        "&:hover": {
                          backgroundColor: "#dc2626 !important",
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Car Display Area */}
            <div className="lg:col-span-8 xl:col-span-9">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center p-16 bg-white border shadow-xl rounded-3xl border-slate-100">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 border-4 border-slate-200 border-t-[#f5272b] rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="fa-solid fa-car text-[#f5272b] text-3xl"></i>
                    </div>
                  </div>
                  <p className="text-lg font-medium text-slate-600">Loading vehicles...</p>
                </div>
              ) : selectedCar ? (
                <div className="animate-fadeIn">
                  <CarBox data={selectedCar} />
                </div>
              ) : (
                <div className="p-16 text-center bg-white border shadow-xl rounded-3xl border-slate-100">
                  <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 rounded-full bg-slate-100">
                    <i className="text-4xl fa-solid fa-car-side text-slate-400"></i>
                  </div>
                  <h3 className="mb-3 text-2xl font-bold text-slate-900">No Vehicle Selected</h3>
                  <p className="text-slate-600">Please select a vehicle from the list to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </section>
  );
}

export default CarSlide;

function paginator(items, current_page, per_page_items) {
  let page = current_page || 1,
    per_page = per_page_items,
    offset = (page - 1) * per_page,
    paginatedItems = items.slice(offset).slice(0, per_page_items),
    total_pages = Math.ceil(items.length / per_page);

  return {
    page: page,
    per_page: per_page,
    pre_page: page - 1 ? page - 1 : null,
    next_page: total_pages > page ? page + 1 : null,
    total: items.length,
    total_pages: total_pages,
    data: paginatedItems,
  };
}
